var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchDiscoveredColorways, StyleLoadError, switchCustomizerColorway, } from '@/api/styleAggregator';
import { setStyleSceneFromStyleAggregatorData } from '@/actions/setScene';
import { reconcileLogosForNextColorway } from '@/domain/logos/reconcileLogoRegions';
import { setMockupLogos, setRecipeExportState, setStyle, setStylePreviewUrl, } from '@/store/appSlice';
function recipeIdentity(source) {
    if (!source || typeof source !== 'object' || Array.isArray(source))
        return null;
    const record = source;
    const brand = typeof record.brand === 'string' ? record.brand.trim() : '';
    const number = typeof record.number === 'string' ? record.number.trim() : '';
    if (!brand || !number)
        return null;
    return { brand, number };
}
function withRecipeColor(source, color) {
    if (!source || typeof source !== 'object' || Array.isArray(source))
        return source;
    return Object.assign(Object.assign({}, source), { color });
}
export function useColorwayPicker() {
    var _a, _b, _c;
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const apiKey = (_a = searchParams.get('api_key')) !== null && _a !== void 0 ? _a : '';
    const sessionToken = useSelector((state) => state.app.sessionToken);
    const colorway = useSelector((state) => state.app.colorway);
    const recipeSource = useSelector((state) => state.app.recipeSource);
    const logos = useSelector((state) => state.app.mockupLogos);
    const baseline = useSelector((state) => state.app.recipeLogoBaseline);
    const regions = useSelector((state) => state.app.styleRegions);
    const pointsOfMeasure = useSelector((state) => state.app.pointsOfMeasure);
    const hideTearAwayLabel = useSelector((state) => state.app.mockupHideTearAwayLabel);
    const sessionTokenExpiresAt = useSelector((state) => state.app.sessionTokenExpiresAt);
    const [colors, setColors] = useState([]);
    const [error, setError] = useState(null);
    const [switching, setSwitching] = useState(false);
    const identity = recipeIdentity(recipeSource);
    const brand = (_b = identity === null || identity === void 0 ? void 0 : identity.brand) !== null && _b !== void 0 ? _b : '';
    const styleNumber = (_c = identity === null || identity === void 0 ? void 0 : identity.number) !== null && _c !== void 0 ? _c : '';
    useEffect(() => {
        if (!sessionToken || !apiKey || !brand || !styleNumber) {
            setColors([]);
            return;
        }
        const controller = new AbortController();
        void (() => __awaiter(this, void 0, void 0, function* () {
            try {
                const discovered = yield fetchDiscoveredColorways({
                    apiKey,
                    brand,
                    styleNumber,
                    signal: controller.signal,
                });
                if (controller.signal.aborted)
                    return;
                setColors(discovered);
                setError(null);
            }
            catch (fetchError) {
                if (fetchError instanceof DOMException && fetchError.name === 'AbortError')
                    return;
                setColors([]);
                setError(fetchError instanceof Error ? fetchError.message : 'Failed to list colorways');
            }
        }))();
        return () => {
            controller.abort();
        };
    }, [apiKey, brand, sessionToken, styleNumber]);
    const colorOptions = useMemo(() => {
        const names = new Set(colors);
        if (colorway)
            names.add(colorway);
        return [...names].sort((left, right) => left.localeCompare(right));
    }, [colorway, colors]);
    const selectColorway = (nextColor) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!sessionToken || !apiKey || !nextColor || nextColor === colorway || switching)
            return;
        setSwitching(true);
        setError(null);
        try {
            const next = yield switchCustomizerColorway({
                apiKey,
                sessionToken,
                color: nextColor,
            });
            const nextRegions = next.styleRegions;
            const nextPoms = (_a = next.pointsOfMeasure) !== null && _a !== void 0 ? _a : [];
            const rematched = reconcileLogosForNextColorway(logos, regions, nextRegions, pointsOfMeasure, nextPoms);
            const rematchedBaseline = baseline
                ? reconcileLogosForNextColorway(baseline, regions, nextRegions, pointsOfMeasure, nextPoms)
                : rematched;
            const nextColorway = (_b = next.colorway) !== null && _b !== void 0 ? _b : nextColor;
            dispatch(setMockupLogos({
                logos: rematched,
                hideTearAwayLabel,
            }));
            dispatch(setRecipeExportState({
                recipeSource: withRecipeColor(recipeSource, nextColorway),
                recipeLogoBaseline: rematchedBaseline,
                pointsOfMeasure: nextPoms,
                colorway: nextColorway,
                sessionToken,
                sessionTokenExpiresAt,
            }));
            dispatch(setStyle(next.style));
            const preview = (_c = next.style) === null || _c === void 0 ? void 0 : _c.preview;
            dispatch(setStylePreviewUrl(preview !== null && preview !== void 0 ? preview : null));
            yield setStyleSceneFromStyleAggregatorData(dispatch, next, hideTearAwayLabel, Boolean(next.isBrandApproved));
        }
        catch (switchError) {
            if (switchError instanceof StyleLoadError || switchError instanceof Error) {
                setError(switchError.message);
            }
            else {
                setError('Failed to switch colorway');
            }
        }
        finally {
            setSwitching(false);
        }
    });
    return {
        colorOptions,
        colorway,
        error,
        selectColorway,
        sessionToken,
        switching,
    };
}
